/**
 * A header for content pages (articles and videos) containing the title and
 * a series of clickable breadcrumbs.
 */

import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import {color} from "@khanacademy/wonder-blocks-tokens";
import type {Standard} from "@khan/content-types";
import {RequestInfo} from "@khan/shared-core";
import {contentWidth, mediaQueries} from "@khan/shared-styles";
import {Standards} from "@khan/standards-ui";

// eslint-disable-next-line no-restricted-imports -- TODO: Replace this component with the equivalent one from Wonder Blocks.
import DeprecatedBreadcrumbs, {
    type SingleBreadcrumb,
} from "#components/breadcrumbs-package/deprecated-breadcrumbs.tsx";
import * as sharedStyles from "#content-library-legacy/styles/shared.ts";
import {RenderWithContentLibraryNavigationEvents} from "#content-library/cedar-events.tsx";
import exerciseConstants from "#exercises-components-package/style-constants.ts";
// eslint-disable-next-line no-restricted-imports -- TODO: Replace this component with the equivalent one from Wonder Blocks.
import {deprecatedTypography} from "#shared-styles-package/deprecated-typography.ts";

import ContentDescription from "./content-description.tsx";
import EditorShortcuts from "./editor-shortcuts.tsx";

type ContentDescriptionProps = JSX.LibraryManagedAttributes<
    typeof ContentDescription,
    React.ComponentProps<typeof ContentDescription>
>;

type Props = {
    authorList: ContentDescriptionProps["authorList"];
    breadcrumbs: Array<SingleBreadcrumb>;
    description?: ContentDescriptionProps["description"];
    editContentUrl?: string;
    hideDescriptionOnMobile?: ContentDescriptionProps["hideDescriptionOnMobile"];
    // Whether to show a link to the editor(s) for this piece of content
    showEditorShortcuts?: boolean;
    standards?: Array<Standard>;
    // Should standards descriptions (tooltips) be shown for learners?
    showStandardsDescriptionsForLearners?: boolean;
    title: React.ReactNode;
};

class ContentPageHeader extends React.Component<Props> {
    static defaultProps: {
        authorList: Array<any>;
        showEditorShortcuts: boolean;
        showStandardsDescriptionsForLearners: boolean;
    } = {
        authorList: [],
        showEditorShortcuts: false,
        showStandardsDescriptionsForLearners: true,
    };

    render(): React.ReactNode {
        const {
            authorList,
            breadcrumbs,
            editContentUrl,
            description,
            hideDescriptionOnMobile,
            showEditorShortcuts,
            standards,
            title,
            showStandardsDescriptionsForLearners,
        } = this.props;

        // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
        const hasDescription = description || authorList.length > 0;
        const hasStandards = standards && standards.length > 0;

        const titleModuleClasses = css(
            styles.titleModule,
            (hasDescription || hasStandards) && styles.titleModulePadding,
        );

        return (
            <div className={css(styles.header)}>
                <div className={css(styles.titleAndDescription)}>
                    <div className={titleModuleClasses}>
                        <div className={css(styles.breadcrumbRow)}>
                            <DeprecatedBreadcrumbs
                                isNavigation={true}
                                path={breadcrumbs}
                                separator={"\u00b7"}
                                style={styles.breadcrumbs}
                                lastItemStyle={styles.breadcrumbLastItem}
                                separatorStyle={styles.separator}
                            />
                        </div>

                        <div className={css(styles.titleAndLogo)}>
                            <h1 className={css(styles.title)}>
                                {title}
                                {showEditorShortcuts && (
                                    <EditorShortcuts
                                        editContentUrl={editContentUrl}
                                        kaLocale={RequestInfo.kaLocale}
                                    />
                                )}
                            </h1>
                        </div>
                    </div>
                    {hasStandards && (
                        <RenderWithContentLibraryNavigationEvents>
                            {({standardsClicked}) => (
                                <div
                                    className={css(
                                        hasDescription &&
                                            styles.titleModulePadding,
                                    )}
                                >
                                    <Standards
                                        standards={standards}
                                        showDescriptionsForLearners={
                                            showStandardsDescriptionsForLearners
                                        }
                                        handleStandardClick={standardsClicked}
                                    />
                                </div>
                            )}
                        </RenderWithContentLibraryNavigationEvents>
                    )}
                    {hasDescription && (
                        <ContentDescription
                            authorList={authorList}
                            description={description}
                            hideDescriptionOnMobile={hideDescriptionOnMobile}
                        />
                    )}
                </div>
            </div>
        );
    }
}

const styles = StyleSheet.create({
    header: {
        ...sharedStyles.contentPadding,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
    },

    titleAndDescription: {
        flexDirection: "column",

        width: contentWidth,
        [mediaQueries.smOrSmaller]: {
            width: "100%",
        },
    },

    titleModule: {
        paddingTop: 32,
        paddingBottom: 2,
        textAlign: "left",
    },

    titleModulePadding: {
        paddingBottom: 12,
    },

    titleAndLogo: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "nowrap",
    },

    // eslint-disable-next-line react-native/no-unused-styles
    breadcrumbsBackArrow: {
        marginRight: 1,
    },

    breadcrumbs: {
        // Prevent drawing area from showing behind this element
        backgroundColor: color.white,
        zIndex: exerciseConstants.zIndexes.aboveDrawingArea,
        color: color.offBlack64,

        [mediaQueries.smOrSmaller]: {
            display: "none",
        },
    },

    separator: {
        padding: "0 0.5em",
        [mediaQueries.smOrSmaller]: {
            display: "none",
        },
    },

    breadcrumbLastItem: {
        [mediaQueries.smOrSmaller]: {
            display: "inline-flex",
        },
    },

    breadcrumbRow: {
        marginBottom: 6,

        // It's assumed that other navigation elements are visible in these
        // larger screens, hence the hiding of the breadcrumbs.
        [mediaQueries.lg]: {
            display: "none",
        },
        [mediaQueries.xl]: {
            display: "none",
        },
    },

    title: {
        ...deprecatedTypography.conceptHeadingDesktop,
        color: color.offBlack,
        margin: 0,
        position: "relative",

        [mediaQueries.mdOrSmaller]: {
            ...deprecatedTypography.conceptHeadingMobile,
        },
    },
    // eslint-disable-next-line react-native/no-unused-styles
    logo: {
        marginLeft: 16,
    },
});

export default ContentPageHeader;
